import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowBack, ArrowForward, Delete, Upload } from '@mui/icons-material'
import { Box, Button, Card, CardActions, CardMedia, FormHelperText, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { usePostPublicFilesMutation } from '../../../../../api/core'
import { MAX_PUBLIC_UPLOAD_FILE_SIZE_MB, partitionFilesByMaxSize } from '../../../../helpers/fileUploadLimits'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { moveItem, prepareReportCover } from '../../helpers/reportHelpers'
import { useTrackReportUpload } from '../../helpers/reportUploads'

type ReportCoversFieldProps = {
  value: string[]
  onChange: (coverUrls: string[]) => void
  error?: boolean
  disabled?: boolean
}

export const ReportCoversField: FC<ReportCoversFieldProps> = ({ value, onChange, error, disabled }) => {
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const [uploadFiles] = usePostPublicFilesMutation()
  // Covers the conversion as well as the upload.
  const [isUploading, setIsUploading] = useState(false)
  useTrackReportUpload('coverUrls', isUploading)
  const isLocked = disabled || isUploading

  const upload = async (selected: File[]) => {
    if (!selected.length) return
    const images = selected.filter((file) => file.type.startsWith('image/'))
    const { accepted, rejected } = partitionFilesByMaxSize(images)
    if (rejected.length || images.length < selected.length) {
      notifyError(t('internal-analysts:reports_covers_skipped', { size: MAX_PUBLIC_UPLOAD_FILE_SIZE_MB }))
    }
    if (!accepted.length) return
    setIsUploading(true)
    try {
      const files = await Promise.all(accepted.map(prepareReportCover))
      const uploaded = await uploadFiles({ files, type: 'reportCover', route: 'public' }).unwrap()
      onChange([...value, ...uploaded.map(({ url }) => url).filter(Boolean)])
      notifySuccess(t('internal-analysts:reports_covers_uploaded'))
    } catch {
      notifyError(t('internal-common:image_upload_failed'))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        {t('internal-analysts:reports_covers')} *
      </Typography>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        {value.map((url, index) => (
          <Card key={`${url}-${index}`} variant="outlined" sx={{ width: 160 }}>
            <CardMedia
              component="img"
              image={url}
              alt={t('internal-analysts:reports_cover_number', { number: index + 1 })}
              sx={{ height: 110, objectFit: 'contain' }}
            />
            <CardActions sx={{ justifyContent: 'space-between' }}>
              <Tooltip title={t('internal-analysts:reports_cover_move_earlier')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t('internal-analysts:reports_cover_move_earlier')}
                    onClick={() => onChange(moveItem(value, index, index - 1))}
                    disabled={isLocked || index === 0}
                  >
                    <ArrowBack fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('internal-analysts:reports_cover_move_later')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t('internal-analysts:reports_cover_move_later')}
                    onClick={() => onChange(moveItem(value, index, index + 1))}
                    disabled={isLocked || index === value.length - 1}
                  >
                    <ArrowForward fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('internal-analysts:reports_cover_remove')}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={t('internal-analysts:reports_cover_remove')}
                    onClick={() => onChange(value.filter((_url, urlIndex) => urlIndex !== index))}
                    disabled={isLocked}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </CardActions>
          </Card>
        ))}
        {isUploading && <Skeleton variant="rounded" width={160} height={160} />}
      </Stack>
      <Button component="label" variant="outlined" startIcon={<Upload />} disabled={isLocked} sx={{ mt: value.length || isUploading ? 2 : 0 }}>
        {t('internal-analysts:reports_covers_upload')}
        <input
          hidden
          type="file"
          multiple
          accept="image/*"
          aria-label={t('internal-analysts:reports_covers')}
          onChange={(event) => {
            upload(Array.from(event.target.files ?? []))
            event.target.value = ''
          }}
        />
      </Button>
      <FormHelperText error={error}>{error ? t('internal-analysts:reports_covers_required') : t('internal-analysts:reports_covers_help')}</FormHelperText>
    </Box>
  )
}
